import { useState, useEffect } from "react";
import API from "@/lib/api";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { UserPlus, Clock, CheckCircle2, XCircle, Trash2, Copy, Loader2, Link as LinkIcon, Unlock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export default function TeamTab({ currentUserId }) {
  const [providers, setProviders] = useState([]);
  const [invites, setInvites] = useState([]);
  const [requests, setRequests] = useState([]);
  const [practitioners, setPractitioners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviting, setInviting] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // {type, id, label, onConfirm}
  const [confirmBusy, setConfirmBusy] = useState(false);
  const { toast } = useToast();

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, i, r, pr, pt] = await Promise.all([
        API.get("/provider/patients"),
        API.get("/provider-admin/invites"),
        API.get("/provider-admin/access-requests"),
        API.get("/provider-admin/providers"),
        API.get("/provider/intakeq-practitioners"),
      ]);
      setProviders(pr.data || []);
      setInvites(i.data || []);
      setRequests(r.data || []);
      setPractitioners(pt.data || []);
      // Merge practitioner link from /provider/patients.providers if needed
      const ppMap = {};
      (p.data?.providers || []).forEach(pv => { ppMap[pv.id] = pv.intakeq_practitioner_id; });
      setProviders(prev => prev.map(pv => ({ ...pv, intakeq_practitioner_id: ppMap[pv.id] ?? pv.intakeq_practitioner_id })));
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    setInviting(true);
    try {
      await API.post("/provider-admin/invites", { email: inviteEmail, name: inviteName });
      toast({ title: "Invite sent", description: `${inviteEmail} will get an email with instructions.`, duration: 3500 });
      setInviteEmail(""); setInviteName("");
      refresh();
    } catch (err) {
      toast({ title: "Couldn't send invite", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setInviting(false); }
  };

  const revokeInvite = (invite) => {
    setConfirmAction({
      type: "revoke-invite",
      title: "Revoke invitation?",
      description: `The invitation link sent to ${invite.email} will stop working. They won't be able to accept it.`,
      confirmLabel: "Revoke invitation",
      destructive: true,
      run: async () => {
        await API.delete(`/provider-admin/invites/${invite.id}`);
        toast({ title: "Invitation revoked" });
      },
    });
  };

  const removeProvider = (provider) => {
    setConfirmAction({
      type: "remove-provider",
      title: `Remove ${provider.full_name || provider.email}?`,
      description: `${provider.email} will lose provider access immediately. Their account reverts to a patient account and they can no longer see any patient data.`,
      confirmLabel: "Remove provider access",
      destructive: true,
      run: async () => {
        await API.delete(`/provider-admin/providers/${provider.id}`);
        toast({ title: "Provider access removed" });
      },
    });
  };

  const runConfirm = async () => {
    if (!confirmAction) return;
    setConfirmBusy(true);
    try {
      await confirmAction.run();
      setConfirmAction(null);
      refresh();
    } catch (err) {
      toast({
        title: "Couldn't complete action",
        description: err?.response?.data?.detail || err.message || "Try again",
        variant: "destructive",
      });
    } finally {
      setConfirmBusy(false);
    }
  };

  const reviewRequest = async (id, action) => {
    try {
      await API.put(`/provider-admin/access-requests/${id}`, { action });
      toast({ title: action === "approve" ? "Request approved — invite sent" : "Request denied" });
      refresh();
    } catch (err) {
      toast({ title: "Couldn't update", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/accept-invite?token=${token}`;
    navigator.clipboard.writeText(link);
    toast({ title: "Invite link copied", description: link, duration: 3500 });
  };

  const unlockAccount = async (e) => {
    e.preventDefault();
    const email = unlockEmail.trim().toLowerCase();
    if (!email) return;
    setUnlocking(true);
    try {
      const { data } = await API.post("/provider-admin/unlock-account", { email });
      toast({
        title: "Account unlocked",
        description: `${email} — cleared ${data.login_attempts_cleared} login attempt(s) and ${data.reset_attempts_cleared} reset attempt(s). They can try again now.`,
        duration: 5000,
      });
      setUnlockEmail("");
    } catch (err) {
      toast({
        title: "Couldn't unlock",
        description: err?.response?.data?.detail || "Try again",
        variant: "destructive",
      });
    } finally {
      setUnlocking(false);
    }
  };

  const linkPractitioner = async (providerId, practitionerId) => {
    try {
      await API.put(`/provider/providers/${providerId}/intakeq-practitioner`, { intakeq_practitioner_id: practitionerId });
      toast({ title: practitionerId ? "Linked to IntakeQ practitioner" : "Unlinked", duration: 3000 });
      refresh();
    } catch (err) {
      toast({ title: "Couldn't update link", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  const pendingRequests = requests.filter(r => r.status === "pending");
  const pendingInvites = invites.filter(i => i.status === "pending");

  return (
    <div className="space-y-6" data-testid="team-tab">
      {/* Invite form */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2">
          <UserPlus className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold">Invite a provider</p>
        </div>
        <p className="text-[11px] text-muted-foreground">Only you (the super admin) can invite new providers.</p>
        <form onSubmit={sendInvite} className="space-y-2" data-testid="invite-provider-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <Label className="text-[11px] text-muted-foreground">Name (optional)</Label>
              <Input data-testid="invite-name-input" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder="Dr. Jane Smith" className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-[11px] text-muted-foreground">Email</Label>
              <Input data-testid="invite-email-input" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="jane@example.com" className="h-9 text-sm" required />
            </div>
          </div>
          <Button type="submit" size="sm" className="rounded-full gap-1.5 h-8 text-xs" disabled={inviting} data-testid="invite-send-button">
            {inviting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
            Send invite
          </Button>
        </form>
      </div>

      {/* Unlock account (brute-force lockout clearer) */}
      <div className="bg-card border border-border rounded-xl p-4 space-y-3" data-testid="unlock-account-card">
        <div className="flex items-center gap-2">
          <Unlock className="w-4 h-4 text-primary" />
          <p className="text-sm font-semibold">Unlock a locked-out account</p>
        </div>
        <p className="text-[11px] text-muted-foreground">
          If a patient or provider hits the 5-strike / 15-minute lockout after too many failed login attempts,
          enter their email to clear it immediately. Works on the account they used to sign in.
        </p>
        <form onSubmit={unlockAccount} className="flex flex-col sm:flex-row gap-2" data-testid="unlock-account-form">
          <Input
            data-testid="unlock-email-input"
            type="email"
            value={unlockEmail}
            onChange={e => setUnlockEmail(e.target.value)}
            placeholder="user@example.com"
            className="h-9 text-sm flex-1"
            required
          />
          <Button type="submit" size="sm" variant="outline" className="rounded-full gap-1.5 h-9 text-xs" disabled={unlocking} data-testid="unlock-account-button">
            {unlocking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
            Unlock account
          </Button>
        </form>
      </div>

      {/* Pending access requests */}
      {pendingRequests.length > 0 && (
        <div className="space-y-2" data-testid="pending-access-requests">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Pending access requests ({pendingRequests.length})</p>
          {pendingRequests.map(r => (
            <div key={r.id} className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-medium">{r.name}</p>
                  <p className="text-xs text-muted-foreground">{r.email}</p>
                  {r.reason && <p className="text-xs text-muted-foreground mt-1 italic">"{r.reason}"</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" className="h-7 text-xs rounded-full gap-1" onClick={() => reviewRequest(r.id, "approve")} data-testid="approve-request-button">
                    <CheckCircle2 className="w-3 h-3" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs rounded-full gap-1" onClick={() => reviewRequest(r.id, "deny")} data-testid="deny-request-button">
                    <XCircle className="w-3 h-3" /> Deny
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pending invites */}
      {pendingInvites.length > 0 && (
        <div className="space-y-2" data-testid="pending-invites">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Pending invites ({pendingInvites.length})</p>
          {pendingInvites.map(inv => (
            <div key={inv.id} className="bg-card border border-border rounded-xl p-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium">{inv.name || inv.email}</p>
                <p className="text-xs text-muted-foreground">{inv.email}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                  <Clock className="w-2.5 h-2.5" /> expires {new Date(inv.expires_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                </p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full gap-1" onClick={() => copyInviteLink(inv.token)} data-testid="copy-invite-link-button">
                  <Copy className="w-3 h-3" /> Copy link
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full gap-1 text-destructive" onClick={() => revokeInvite(inv)} data-testid="revoke-invite-button">
                  <Trash2 className="w-3 h-3" /> Revoke
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Active providers */}
      <div className="space-y-2" data-testid="active-providers">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Active providers ({providers.length})</p>
        <p className="text-[11px] text-muted-foreground -mt-1">Link each provider to their IntakeQ practitioner to enable auto-assignment.</p>
        {providers.map(p => (
          <div key={p.id} className="bg-card border border-border rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <Avatar src={p.avatar_url} name={p.full_name} email={p.email} size="sm" className="shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{p.full_name || p.email}
                    {p.is_super_admin && <span className="ml-2 text-[10px] text-primary bg-primary/10 border border-primary/20 rounded-full px-1.5 py-0.5 font-semibold">Super admin</span>}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{p.email}</p>
                </div>
              </div>
              {!p.is_super_admin && p.id !== currentUserId && (
                <Button size="sm" variant="ghost" className="h-7 text-xs rounded-full text-destructive shrink-0" onClick={() => removeProvider(p)} data-testid="remove-provider-button">
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2 pl-1">
              <LinkIcon className="w-3 h-3 text-muted-foreground" />
              <p className="text-[11px] text-muted-foreground whitespace-nowrap">IntakeQ practitioner:</p>
              <Select
                value={p.intakeq_practitioner_id || "__none__"}
                onValueChange={(v) => linkPractitioner(p.id, v === "__none__" ? null : v)}
              >
                <SelectTrigger className="h-7 text-xs flex-1" data-testid={`link-practitioner-${p.id}`}>
                  <SelectValue placeholder="Not linked" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not linked</SelectItem>
                  {practitioners.map(pr => (
                    <SelectItem key={pr.id} value={pr.id}>{pr.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && !confirmBusy && setConfirmAction(null)}>
        <AlertDialogContent data-testid="team-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmAction?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirmAction?.description}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirmBusy} data-testid="confirm-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runConfirm(); }}
              disabled={confirmBusy}
              className={confirmAction?.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
              data-testid="confirm-action-button"
            >
              {confirmBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : confirmAction?.confirmLabel || "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
